import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, ChevronDown, MapPin, FileText, User, Image as ImageIcon } from 'lucide-react';
import TopBar from '../components/TopBar';
import BottomNav from '../components/BottomNav';
import AdBanner from '../components/AdBanner';
import { listEvents, eventTypeMeta } from '../lib/events';

const MONTHS_ES = ['Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio', 'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'];
const DAY_ABBR = ['DOM', 'LUN', 'MAR', 'MIE', 'JUE', 'VIE', 'SAB'];

export default function Agenda() {
    const navigate = useNavigate();
    const today = new Date();
    const [selected, setSelected] = useState(today);
    const [events, setEvents] = useState([]);
    const [loading, setLoading] = useState(true);

    const monthStart = new Date(today.getFullYear(), today.getMonth(), 1);
    const monthEnd = new Date(today.getFullYear(), today.getMonth() + 6, 0);

    useEffect(() => {
        listEvents(toIsoDate(monthStart), toIsoDate(monthEnd))
            .then((d) => setEvents(d.events || []))
            .finally(() => setLoading(false));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const dayStrip = useMemo(() => {
        const arr = [];
        for (let i = 0; i < 7; i++) {
            const d = new Date(today);
            d.setDate(today.getDate() + i);
            arr.push(d);
        }
        return arr;
    }, []);

    const eventsByDate = useMemo(() => {
        const m = {};
        for (const e of events) {
            const key = e.occurrence_date || e.date;
            if (!m[key]) m[key] = [];
            m[key].push(e);
        }
        return m;
    }, [events]);

    const dayEvents = eventsByDate[toIsoDate(selected)] || [];
    const upcoming = events.filter((e) => {
        const d = new Date(e.occurrence_date || e.date);
        return d >= new Date(today.getFullYear(), today.getMonth(), today.getDate());
    });

    return (
        <div className="min-h-screen bg-surface app-shell pb-32">
            <div className="px-6">
                <TopBar
                    onBellClick={() => navigate('/notificaciones')}
                    onMenuClick={() => navigate('/home')}
                    onPremiumClick={() => alert('Próximamente: planes Premium')}
                />

                <div className="mt-4">
                    <h1 className="text-3xl font-bold text-brand-navy" data-testid="agenda-title">Agenda</h1>
                    <button className="mt-1 flex items-center gap-1 text-gray-500 text-sm">
                        {MONTHS_ES[today.getMonth()]} {today.getFullYear()}
                        <ChevronDown className="w-4 h-4" />
                    </button>
                </div>

                <div className="mt-4 flex gap-2 overflow-x-auto pb-2">
                    {dayStrip.map((d) => {
                        const isSelected = toIsoDate(d) === toIsoDate(selected);
                        const dots = (eventsByDate[toIsoDate(d)] || []).slice(0, 3);
                        return (
                            <button
                                key={d.toISOString()}
                                onClick={() => setSelected(d)}
                                data-testid={`agenda-day-${toIsoDate(d)}`}
                                className={`min-w-[68px] px-3 py-3 rounded-2xl flex flex-col items-center transition ${isSelected ? 'bg-brand text-white' : 'bg-white text-brand-navy'}`}
                            >
                                <span className={`text-[10px] font-bold uppercase ${isSelected ? 'text-white/80' : 'text-gray-500'}`}>{DAY_ABBR[d.getDay()]}</span>
                                <span className="text-xl font-bold">{d.getDate()}</span>
                                <div className="mt-1 flex gap-1 h-1.5">
                                    {dots.map((e, i) => (
                                        <span key={i} className={`w-1.5 h-1.5 rounded-full ${isSelected ? 'bg-white' : ''}`}
                                            style={!isSelected ? { background: dotColor(e.type) } : {}} />
                                    ))}
                                </div>
                            </button>
                        );
                    })}
                </div>

                <div className="mt-6">
                    <h2 className="text-[11px] font-bold uppercase tracking-wider text-gray-500">
                        Próximos eventos <span className="text-gray-400">({upcoming.length})</span>
                    </h2>
                    <div className="mt-3 space-y-3">
                        {loading ? (
                            <div className="py-10 flex justify-center"><div className="w-8 h-8 border-2 border-brand/30 border-t-brand rounded-full animate-spin" /></div>
                        ) : upcoming.length === 0 ? (
                            <div className="bg-white/60 rounded-2xl p-6 text-center text-sm text-gray-400 border border-dashed border-gray-200">
                                Aún no hay eventos. Toca <strong>"Nuevo evento"</strong> para crear el primero.
                            </div>
                        ) : (
                            upcoming.map((e) => <EventCard key={`${e.id}-${e.occurrence_date}`} event={e} onClick={() => navigate(`/agenda/${e.id}`)} />)
                        )}
                    </div>
                </div>
            </div>

            <button
                onClick={() => navigate('/agenda/nuevo')}
                className="fixed bottom-[150px] left-1/2 -translate-x-1/2 px-6 h-14 bg-brand text-white rounded-full font-bold shadow-brand-cta hover:scale-[1.02] active:scale-[0.97] transition flex items-center gap-2 z-30"
                data-testid="agenda-fab-new"
            >
                <Plus className="w-5 h-5" /> Nuevo evento
            </button>

            <div className="fixed bottom-[72px] left-1/2 -translate-x-1/2 w-full max-w-[480px] z-20"><AdBanner /></div>
            <BottomNav />
        </div>
    );
}

function EventCard({ event, onClick }) {
    const meta = eventTypeMeta(event.type);
    return (
        <button
            onClick={onClick}
            className="w-full bg-white rounded-2xl shadow-card p-4 text-left hover:shadow-card-md active:scale-[0.99] transition"
            data-testid={`event-card-${event.id}`}
        >
            <div className="flex gap-3">
                <div className={`w-12 h-12 rounded-2xl flex items-center justify-center text-xl ${meta.color}`}>
                    {meta.icon}
                </div>
                <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-2">
                        <h3 className="text-base font-bold text-brand-navy truncate flex-1">{event.title}</h3>
                        <span className={`text-[10px] font-bold uppercase px-2 py-0.5 rounded-full ${meta.color}`}>{meta.label}</span>
                    </div>
                    <p className="text-sm text-gray-500 mt-0.5">{formatDateLabel(event.occurrence_date || event.date)}{event.time ? ` · ${event.time}` : ''}</p>
                    {event.description && <p className="text-sm text-gray-600 mt-1 line-clamp-1">{event.description}</p>}
                    {event.amount && <p className="text-sm font-semibold text-emerald-600 mt-1">${event.amount.toFixed(2)} – Vencimiento</p>}
                    <div className="mt-2 flex items-center gap-1.5 text-gray-400">
                        {event.image_url && <span className="w-6 h-6 bg-gray-100 rounded-md flex items-center justify-center"><ImageIcon className="w-3.5 h-3.5" /></span>}
                        {event.description && <span className="w-6 h-6 bg-gray-100 rounded-md flex items-center justify-center"><FileText className="w-3.5 h-3.5" /></span>}
                        {event.address && <span className="w-6 h-6 bg-gray-100 rounded-md flex items-center justify-center"><MapPin className="w-3.5 h-3.5" /></span>}
                        {(event.notify_members || []).length > 0 && <span className="w-6 h-6 bg-gray-100 rounded-md flex items-center justify-center"><User className="w-3.5 h-3.5" /></span>}
                    </div>
                </div>
            </div>
        </button>
    );
}

function toIsoDate(d) {
    return d.toISOString().slice(0, 10);
}
function dotColor(type) {
    switch (type) {
        case 'cumpleanos': case 'celebracion': return '#EC4899';
        case 'salud': case 'medico': return '#3B82F6';
        case 'pago': return '#10B981';
        case 'escolar': return '#6366F1';
        case 'trabajo': return '#F59E0B';
        case 'viaje': return '#06B6D4';
        default: return '#9CA3AF';
    }
}
function formatDateLabel(iso) {
    const d = new Date(iso);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const target = new Date(d); target.setHours(0, 0, 0, 0);
    const diff = Math.round((target - today) / 86400000);
    if (diff === 0) return 'Hoy';
    if (diff === 1) return 'Mañana';
    if (diff === -1) return 'Ayer';
    return d.toLocaleDateString('es-ES', { weekday: 'long', day: 'numeric', month: 'short' });
}
